import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import type { HatchProp, DrawingEntityProps } from "../types/NoteConfig";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, findEntityByHandle } from "../core/oda";
import { OpenMode } from "../core/constants";
import { UNDO_LAYER_NAME, ensureUndoLayerExists } from "../database/layers";
import { createHatchFromEntity } from "../database/hatch";
import { recordGraphicsModified } from "../entities";
import { stampCreationMeta } from "../meta/capture";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

export interface ClosePolylineWithHatchParams {
  /** The OdDbObjectId of the polyline entity */
  entityId: DrawingModule.OdDbObjectId;
  /** Hatch configuration (optional) */
  hatchConfig?: HatchProp;
  /** Entity props for the hatch */
  hatchProps?: DrawingEntityProps;
}

/**
 * Command for closing a polyline and optionally creating a hatch.
 * Both operations are treated as a single undo/redo operation.
 */
export class ClosePolylineWithHatchCommand implements ICommand {
  private executed: boolean = false;
  private viewer: DRDrawingViewer;
  private handle: string;
  private hatchConfig?: HatchProp;
  private hatchProps?: DrawingEntityProps;
  private previousClosed: boolean = false;
  private hatchHandle: string | null = null;
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: ClosePolylineWithHatchParams) {
    this.viewer = viewer;
    this.handle = getEntityHandle(params.entityId);
    this.hatchConfig = params.hatchConfig;
    this.hatchProps = params.hatchProps;
  }

  /** Close the polyline and create hatch */
  execute(): void {
    const db = this.viewer.database;
    if (!db) return;

    // Ensure undo layer exists for potential hatch
    ensureUndoLayerExists(this.viewer.module, db);

    // Close the polyline
    const polyline = this.openPolyline(OpenMode.kForWrite);
    if (!polyline) return;

    const entityId = findEntityByHandle(this.viewer.module, db, this.handle);

    // Store previous state for undo
    this.previousClosed = polyline.isClosed();
    polyline.setClosed(true);
    if (entityId) {
      recordGraphicsModified(this.viewer.module, entityId);
    }
    deleteObj(polyline);

    // Create hatch if configured
    if (this.hatchConfig && entityId) {
      const hatchResult = createHatchFromEntity(
        this.viewer.module,
        db,
        entityId,
        this.hatchConfig,
        this.hatchProps ?? {},
      );
      if (hatchResult) {
        this.hatchHandle = getEntityHandle(hatchResult.entityId);
        deleteObj(hatchResult.entityId);
        deleteObj(hatchResult.hatch);
      }
    }

    if (entityId) deleteObj(entityId);

    // Oplog : MODIFIED pour la polyligne (fermée) + CREATED pour la hachure si créée.
    // La hachure ne passe pas par BaseAddEntityCommand → il faut la stamper nous-mêmes
    // (stampCreationMeta), sinon emitForHandles la prendrait pour du fond de plan.
    const events = oplogEmitter.emitModified(this.viewer, [
      { handle: this.handle, changeKind: "closed" },
    ]);
    if (this.hatchHandle) {
      stampCreationMeta(this.viewer, [this.hatchHandle]);
      events.push(
        ...oplogEmitter.emitForHandles(this.viewer, "CREATED", [
          this.hatchHandle,
        ]),
      );
    }
    this.oplogEvents = events;

    this.executed = true;
    this.viewer.update();
  }

  /** Restore previous state and remove hatch */
  undo(): void {
    // Remove hatch by moving to undo layer
    if (this.hatchHandle) {
      const hatchEntityId = findEntityByHandle(
        this.viewer.module,
        this.viewer.database,
        this.hatchHandle,
      );
      if (hatchEntityId) {
        this.setEntityLayer(hatchEntityId, UNDO_LAYER_NAME);
        deleteObj(hatchEntityId);
      }
    }

    // Restore polyline closed state
    const polyline = this.openPolyline(OpenMode.kForWrite);
    if (polyline) {
      polyline.setClosed(this.previousClosed);
      deleteObj(polyline);
    }

    this.executed = false;
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  /** Re-apply close and restore hatch */
  redo(): void {
    // Close polyline
    const polyline = this.openPolyline(OpenMode.kForWrite);
    if (polyline) {
      polyline.setClosed(true);
      deleteObj(polyline);
    }

    // Restore hatch by moving back to original layer
    if (this.hatchHandle && this.hatchProps?.layername) {
      const hatchEntityId = findEntityByHandle(
        this.viewer.module,
        this.viewer.database,
        this.hatchHandle,
      );
      if (hatchEntityId) {
        this.setEntityLayer(hatchEntityId, this.hatchProps.layername);
        deleteObj(hatchEntityId);
      }
    }

    this.executed = true;
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  /** Clean up - permanently delete hatch if in undo layer */
  dispose(): void {
    // delete if hatch was created on polyline and command was undone (executed)
    if (this.hatchHandle && !this.executed) {
      const hatchEntityId = findEntityByHandle(
        this.viewer.module,
        this.viewer.database,
        this.hatchHandle,
      );
      if (!hatchEntityId) return;
      const entity = openAs(
        hatchEntityId,
        this.viewer.module.OdDbEntity,
        OpenMode.kForWrite,
      );
      deleteObj(hatchEntityId);
      const currentLayer = entity.layer().toString();
      if (currentLayer === UNDO_LAYER_NAME) {
        entity.erase(true);
      }
      deleteObj(entity);
    }
  }

  private openPolyline(
    openMode: DrawingModule.OpenMode,
  ): DrawingModule.OdDbPolyline | null {
    const entityId = findEntityByHandle(
      this.viewer.module,
      this.viewer.database,
      this.handle,
    );
    if (!entityId) return null;
    const polyline = openAs(
      entityId,
      this.viewer.module.OdDbPolyline,
      openMode,
    );
    deleteObj(entityId);
    return polyline;
  }

  private setEntityLayer(
    entityId: DrawingModule.OdDbObjectId,
    layerName: string,
  ): void {
    const entity = openAs(
      entityId,
      this.viewer.module.OdDbEntity,
      OpenMode.kForWrite,
    );
    const module = this.viewer.module;
    const layerStr = new module.OdString();
    layerStr.set(layerName);
    entity.setLayer(layerStr, false, true);
    deleteObj(layerStr);
    recordGraphicsModified(this.viewer.module, entityId);
    deleteObj(entity);
  }
}
